import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 lg:px-12 py-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-6 lg:px-12 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-slate-600 mb-8">
            Last Updated: January 2025
          </p>

          <div className="space-y-8 text-slate-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing and using the services of MatchFrame Editing School Bangalore (&ldquo;School,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services or website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Course Enrollment and Eligibility</h2>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Eligibility</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>No educational qualification is required for enrollment</li>
                <li>No age limit restrictions apply</li>
                <li>Students must have a genuine interest in video/film editing</li>
                <li>Basic computer knowledge is recommended but not mandatory</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-2">Enrollment Process</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Complete the enrollment form with accurate information</li>
                <li>Pay the required course fees as per the chosen payment plan</li>
                <li>Attend a mandatory orientation session (if applicable)</li>
                <li>Agree to follow School policies and guidelines</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Course Fees and Payment</h2>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Fee Structure</h3>
              <p className="mb-4">
                Course fees vary depending on the program selected. Current pricing starts from ₹25,000 for short-term courses. Detailed fee information will be provided during the enrollment consultation.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-2">Payment Terms</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Flexible payment plans and installment options are available</li>
                <li>Payment must be completed before course commencement</li>
                <li>All fees are non-refundable unless otherwise stated in our refund policy</li>
                <li>We accept payments via bank transfer, UPI, credit/debit cards, and cash</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-2">Refund Policy</h3>
              <p className="mb-3 font-semibold text-slate-900">
                No Refund Policy - Due to the nature of educational services provided:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>All course fees are <strong>non-refundable</strong> once payment has been made</li>
                <li>No refunds will be issued if a student chooses to discontinue or withdraw from the course</li>
                <li>Payment confirms your commitment to complete the enrolled program</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-2">Course Deferral</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Students may request to defer their course start date to a different month</li>
                <li>A deferral fee will apply (amount to be communicated by the admissions office)</li>
                <li>Deferral requests must be made in writing to the admissions office</li>
                <li>Deferral is subject to seat availability in the requested batch</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-2">Property Damage Policy</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Students are responsible for any damage caused to MatchFrame property, equipment, or facilities</li>
                <li>Students are also responsible for damage to any property in the building premises</li>
                <li>The cost of repair or replacement will be borne entirely by the student who caused the damage</li>
                <li>Deliberate or negligent damage may result in additional disciplinary action</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Student Responsibilities</h2>
              <p className="mb-4">As a student, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Attend classes regularly and punctually</li>
                <li>Complete assignments and projects within specified deadlines</li>
                <li>Respect instructors, staff, and fellow students</li>
                <li>Use School equipment and facilities responsibly</li>
                <li>Maintain confidentiality of proprietary course materials</li>
                <li>Not record, reproduce, or distribute course content without permission</li>
                <li>Follow the School&apos;s code of conduct and disciplinary policies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. School Responsibilities</h2>
              <p className="mb-4">MatchFrame Editing School commits to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide high-quality instruction by experienced professionals</li>
                <li>Maintain state-of-the-art equipment and facilities</li>
                <li>Offer placement assistance (subject to student performance)</li>
                <li>Provide course certificates upon successful completion</li>
                <li>Support students with portfolio development and career guidance</li>
                <li>Maintain a safe and conducive learning environment</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Intellectual Property</h2>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">School Content</h3>
              <p className="mb-4">
                All course materials, videos, documents, and resources provided by the School are protected by copyright and intellectual property laws. Students may not reproduce, distribute, or sell School content without written permission.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-2">Student Work</h3>
              <p>
                Students retain ownership of their original creative work. However, by enrolling, you grant the School a non-exclusive license to use student projects for promotional and educational purposes, with proper attribution.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Placement Assistance</h2>
              <p className="mb-4">
                We provide 100% placement assistance, which includes:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Resume building and portfolio development</li>
                <li>Interview preparation and mock interviews</li>
                <li>Job referrals to partner companies</li>
                <li>Industry networking opportunities</li>
              </ul>
              <p className="font-semibold text-slate-900">
                Important: Placement assistance does not guarantee job placement. Final hiring decisions rest with prospective employers. Student performance, skills, and market conditions influence placement outcomes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Code of Conduct</h2>
              <p className="mb-4">Students must adhere to the following:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Maintain professional behavior at all times</li>
                <li>Respect diversity and practice inclusivity</li>
                <li>No harassment, bullying, or discrimination of any kind</li>
                <li>No plagiarism or academic dishonesty</li>
                <li>No illegal activities on School premises</li>
                <li>Proper use of lab hours and equipment</li>
              </ul>
              <p className="mt-4">
                Violation of the code of conduct may result in warnings, suspension, or expulsion without refund.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Course Modifications</h2>
              <p>
                The School reserves the right to modify course content, schedules, instructors, or facilities as necessary. We will make reasonable efforts to notify students of significant changes. In case of substantial changes, students may request a transfer to another batch or a refund (subject to conditions).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Limitation of Liability</h2>
              <p>
                MatchFrame Editing School shall not be liable for any indirect, incidental, or consequential damages arising from the use of our services. Our total liability shall not exceed the amount paid by the student for the course. We are not responsible for personal property loss, theft, or damage on School premises.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Privacy and Data Protection</h2>
              <p>
                Your personal information is handled in accordance with our Privacy Policy. By enrolling, you consent to the collection, use, and processing of your data as described in our Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Termination of Services</h2>
              <p className="mb-4">
                The School may terminate a student&apos;s enrollment for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Violation of Terms of Service or code of conduct</li>
                <li>Non-payment of fees</li>
                <li>Disruptive or inappropriate behavior</li>
                <li>Fraudulent enrollment or misrepresentation</li>
              </ul>
              <p className="mt-4">
                Students may voluntarily withdraw from the course, subject to the refund policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Dispute Resolution</h2>
              <p>
                Any disputes arising from these Terms of Service shall be resolved through good-faith negotiation. If unresolved, disputes will be subject to the jurisdiction of courts in Bangalore, Karnataka, India.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Changes to Terms</h2>
              <p>
                We reserve the right to update these Terms of Service at any time. Material changes will be communicated to enrolled students. Continued use of our services after changes constitutes acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Contact Information</h2>
              <p className="mb-4">
                For questions or concerns regarding these Terms of Service, please contact us:
              </p>
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <p className="font-semibold text-slate-900 mb-2">MatchFrame Editing School Bangalore</p>
                <p>Unit #309, 3rd Floor, No. 77/A, Workafella</p>
                <p>Jyothi Nivas College Road, Koramangala</p>
                <p>Bangalore, Karnataka 560095, India</p>
                <p className="mt-4">
                  <strong>Email:</strong> editingschoolbengaluru@gmail.com
                </p>
                <p>
                  <strong>Phone:</strong> +91 96324 06669
                </p>
              </div>
            </section>

            <section className="bg-purple-50 p-6 rounded-xl border-2 border-purple-200">
              <p className="text-slate-900 font-semibold">
                By enrolling in any course at MatchFrame Editing School Bangalore, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
